import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { ContentSection, SectionHeader } from '../components/sections/ContentSection';
import { MinistryCard } from '../components/cards/MinistryCard';
import { SEO } from '../components/SEO';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Ministries() {
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMinistries = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ministries`);
        if (response.ok) {
          const data = await response.json();
          setMinistries(data);
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch ministries:', err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchMinistries();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO 
        title="Ministries"
        description="Explore ministries at Geneva United Methodist Church. Find ways to serve, connect, and grow in your faith journey."
        url="/ministries"
      />
      
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1720186576697-24c1496a07e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxjaHVyY2glMjB3b3JzaGlwJTIwY2hvaXIlMjBzaW5naW5nfGVufDB8fHx8MTc3MzI1NzQ1NXww&ixlib=rb-4.1.0&q=85"
            alt="Our ministries"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="ministries-page-title">
            Our Ministries
          </h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto">
            Discover ways to serve, grow, and connect with our church family.
          </p>
        </div>
      </section>

      {/* Intro */}
      <ContentSection bgColor="bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-lg text-slate-600 leading-relaxed">
            At Geneva United Methodist Church, we believe that everyone has gifts to share 
            and that together we can make a greater impact. Our ministries offer opportunities 
            for worship, learning, service, and fellowship. We invite you to explore and find 
            where God is calling you to serve.
          </p>
        </div>
      </ContentSection>

      {/* Ministry Cards */}
      <ContentSection bgColor="bg-slate-50">
        <SectionHeader 
          title="Ways to Get Involved"
          subtitle="Find your place in our church community"
        />
        {ministries.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ministries.map((ministry, index) => (
              <MinistryCard 
                key={ministry.id || index} 
                title={ministry.title}
                description={ministry.description}
                image={ministry.imageUrl}
                link={ministry.link || "/contact"}
                buttonText={ministry.buttonText || "Get Involved"}
              />
            ))}
          </div>
        ) : (
          <p className="text-center text-slate-500">No ministries to display yet.</p>
        )}
      </ContentSection>

      {/* Call to Action */}
      <ContentSection bgColor="bg-secondary" paddingY="py-16">
        <div className="text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
            Ready to Get Involved?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">
            Contact us to learn more about our ministries and how you can participate.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-white text-secondary px-8 py-4 rounded-full font-semibold hover:bg-slate-50 transition-all hover:shadow-lg"
            data-testid="ministries-contact-btn"
          >
            Contact Us
          </a>
        </div>
      </ContentSection>
    </Layout>
  );
}
